import { formatCurrency } from '@/lib/currency/format'
import type { DisplayCurrency } from '@/lib/currency/map'
import { localeFormatter } from '@/i18n/format'

/**
 * The locale and display currency a surface states its prices in — the pair every money
 * rendering needs, carried together so a call site cannot supply one and forget the other
 * (which is how a converted peso amount ends up labelled in dollars).
 *
 * Both come from the request: the locale from the URL's path prefix, the currency from
 * `renderedCurrency()` — the currency the page's amounts were ACTUALLY stated in, which
 * is the selected one only when a rate was available to convert at.
 */
export interface MoneyFormat {
    locale: string
    currency: DisplayCurrency
}

/**
 * Formats a price with its minor units (e.g. "$12.00").
 */
export function formatPrice(price: number, { locale, currency }: MoneyFormat): string {
    return formatCurrency(price, currency, locale, { fractionDigits: 2 })
}

/**
 * Formats a price without minor units (e.g. "$12").
 */
export function formatPriceWhole(price: number, { locale, currency }: MoneyFormat): string {
    return formatCurrency(price, currency, locale, { fractionDigits: 0 })
}

/**
 * Formats a `YYYY-MM-DD` date string as a short display date (e.g. "Thu, Feb 12").
 *
 * SSR-safe by construction (invariants.md#ssr-safe-rendered-dates): the `Date` is built
 * from explicit Y/M/D parts **at UTC** and read back through a formatter pinned to UTC,
 * so the rendered weekday/month/day equals the input string's calendar date in *every*
 * timezone — identical on the server-zone SSR render and the browser-zone client render.
 * Do NOT switch to `new Date(iso)` (which parses to an instant the local zone then
 * re-projects) or drop the UTC pin: either makes the output timezone-dependent and
 * reintroduces a React #418 hydration mismatch. Input must be a `YYYY-MM-DD` string,
 * never the ambient clock.
 */
export function formatShortDate(iso: string, locale: string): string {
    const [year, month, day] = iso.split('-').map(Number)
    return localeFormatter(locale).dateTime(new Date(Date.UTC(year, month - 1, day)), {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    })
}

/**
 * Formats duration in minutes to a human-readable string (e.g., "2h 30m").
 * Omits the hours part if zero, and the minutes part if zero.
 */
export function formatDuration(minutes: number): string {
    // round to whole minutes first — facts carries raw float durations (e.g. 119.48 min), so
    // an unrounded remainder would render "1h 59.48m" instead of "2h".
    const whole = Math.round(minutes)
    const hours = Math.floor(whole / 60)
    const mins = whole % 60
    if (hours === 0) {
        return `${mins}m`
    }
    if (mins === 0) {
        return `${hours}h`
    }
    return `${hours}h ${mins}m`
}
